import React from "react";
import type { HpTabCarouselProps } from "dds/organisms/hp-tab-carousel/hp-tab-carousel.types";
import { ZipCodeLazy } from "../v3-zip-code/zip-code-lazy.client";
import { APP_BASE_PATH } from "src/utils/app-base-path";
import { splitZipCopy, type ZipCopyFields } from "../v3-zip-code/zip-copy";
import { anyCardReprices, shouldShowZipCode } from "../v3-zip-code/show-zip-code";
import { toRenderablePlanCard } from "./renderable-plan-card";

export type HpSlotProps = HpTabCarouselProps & ZipCopyFields & {
  /** Draws the ZIP Code between the tab tray and the panels. An authored `true` always
   *  draws it; anything else — `false`, an untouched switch, an absent field — defers to
   *  whether any panel card can re-price. The switch forces the control on and never
   *  off; `show-zip-code.ts` carries why. */
  show_zip_code?: boolean | string;
};

/**
 * The HP Tab Carousel's slot props (WAT-12293). `SLOT_FILLERS` runs this before the
 * canvas renders the organism, because the canvas spreads CMS JSON into an organism and
 * JSON cannot carry the node `location_slot` takes.
 *
 * ⚠ THE ORGANISM IS A TYPE-ONLY IMPORT — and here it always was, because the props live
 * in a separate `.types` module. Keep it that way; see `fill-grow-slots.tsx`.
 *
 * The slot is SECTION-level: it sits between the tray and the panels, so one ZIP
 * serves every tab and does not remount as the reader switches.
 *
 * ⚠ `dp_all_in` HAS TWO SOURCES, and the ZIP now depends on it. `hpTabCarouselAdapter`
 * attaches one on the page path (cache-populate), and `dynamicPageAdapter` attaches one
 * at request time over `panels[].cards[]`. Everything below must therefore be
 * idempotent. The page-path source is why this section keeps its ZIP in the VISUAL
 * EDITOR: `app/v3/[...slug]/page.tsx:133` skips `dynamicPageAdapter` when
 * `enableLivePreview` is set, so a carousel fed ONLY by that one — the plan carousel —
 * draws no ZIP there (`design-conflicts/zip-code.mdx`).
 *
 * The slot is GATED BY THE CARDS, on the same rule as the Plan carousel's — the ZIP is
 * offered where it can change a number — but asked ONCE FOR THE SECTION rather than per
 * tab. The slot sits above the tabs and serves all of them, so a per-tab test would
 * make the line blink in and out as the reader switches; any tab that can re-price
 * keeps the control up for every tab, including those holding no plan cards.
 */
export function fillHpSlots({
  /* Dropped: off the wire this can only be a string, which the organism would render
     as the geo line. The visible copy comes from the flat `zip_*` fields. */
  location_slot: _authoredLocationSlot,
  show_zip_code: showZipCode,
  ...props
}: HpSlotProps): HpTabCarouselProps {
  const { copy, rest } = splitZipCopy(props);
  const authoredPanels = Array.isArray(rest.panels) ? rest.panels : [];
  /* `auto` is the SAME re-pricing test the Plan carousel uses — a ZIP is offered where
     it can change a number — only walked a level deeper, because an HP tab owns its own
     card set. One rule across all three carousels, so the CMS team reads one sentence.

     ⚠ READ FROM `authoredPanels`, NOT FROM `panels` BELOW. `toRenderablePlanCard`
     strips `dp_all_in` on its way to the organism, so testing the mapped cards would
     answer "nothing re-prices" on every page.

     ⚠ ONE ANSWER FOR THE WHOLE SECTION, not one per tab. The slot is section-level, so
     a per-tab test would make the line blink in and out as the reader switches. Any tab
     that can re-price keeps the control up for all of them. */
  const showZip = shouldShowZipCode(
    showZipCode,
    authoredPanels.some(panel => anyCardReprices(panel?.cards))
  );

  /* Cards go through the SAME `toRenderablePlanCard` as the Plan carousel's — see
     there for why the price split is load-bearing. The only difference is the walk:
     HP's cards sit a level deeper, because a tab owns its own card set.

     ⚠ THE CARDS RE-PRICE EVEN WHEN THIS SECTION DRAWS NO ZIP. `zipStore` is
     page-wide, so a ZIP committed in a sibling carousel moves these cards too. Gating
     the leaf on `showZip` would freeze them at the default ZIP beside sibling cards
     showing the visitor's — a wrong number, not a stale one. The cost of the leaves
     is recorded on `design-conflicts/hp-tab-carousel.mdx` instead. */
  const panels = authoredPanels.map(panel => {
    const cards = panel?.cards;
    if (!Array.isArray(cards)) {
      return panel;
    }
    return {
      ...panel,
      cards: cards.map(toRenderablePlanCard)
    };
  });

  return {
    ...rest,
    panels,
    /* ⚠ AFTER the spread, and destructured off above. Either alone keeps authored
       text out; both, so removing one does not hand the slot to CMS. */
    location_slot: showZip ?
      <ZipCodeLazy {...copy} autoOpenOnFirstVisit={false} localChannelsBasePath={APP_BASE_PATH} /> :
      undefined
  } as HpTabCarouselProps;
}

export default fillHpSlots;
